/**
 * Modal.tsx
 *
 * This new Modal in-houses the css around the overlay, container and content, along with the custom css passed to the Component
 * This component also handles the mouse events and keyboard events and dispatch actions related to the modal
 * Migrated from @reach/dialog to @radix-ui/react-dialog (Reach UI is unmaintained)
 */
import React from "react";

import classNames from "classnames";
import * as Dialog from "@radix-ui/react-dialog";

import { ExitIcon } from "./icons/ExitIcon";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ModalProps } from "./types/modalTypes";

import "./styles/Modal.css";
import {
  MODAL_BACK_BUTTON,
  MODAL_CLOSE_BUTTON,
  MODAL_TITLE,
} from "../../../test-identifiers";

export const Modal = ({
  name,
  onBack,
  onClose,
  children,
  visible = false,
  disabled = true,
  hasOverlay = true,
  overlayClass = "",
  containerClass = "",
  hasHeader = true,
  hasBackButton = false,
  backButtonClass = "",
  hasTitle = true,
  title = "",
  titleClass = "",
  hasCloseButton = true,
  closeButtonClass = "",
  contentClass = "",
}: ModalProps): React.ReactElement => {
  if (!visible) {
    return <></>;
  }

  const customModalOverlayClass = {
    fullPage: hasOverlay,
    center: !hasOverlay,
    disabled: disabled,
  };
  const customModalTitleClass = {
    isBothButtonsVisible: hasBackButton && hasCloseButton,
    isOneButtonVisible: hasBackButton !== hasCloseButton,
  };
  const customModalContentClass = { noHeader: !hasHeader };

  return (
    <Dialog.Root
      open={visible}
      onOpenChange={(open) => !open && hasCloseButton && onClose()}
      // Disable body scroll lock for modals without overlay (e.g., InviteFriendsModal)
      // so users can interact with elements behind the modal
      modal={hasOverlay}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          id={`modal-${name}`}
          className={classNames(
            "modalOverlay",
            customModalOverlayClass,
            overlayClass,
          )}
          // When no overlay, allow clicks to pass through to elements behind
          style={!hasOverlay ? { pointerEvents: "none" } : undefined}
        />
        <Dialog.Content
          className={classNames("modalContainer", containerClass)}
        >
          {hasHeader && (
            <div className="modalHeaderWrapper">
              <header className="modalHeader">
                {hasBackButton && (
                  <button
                    data-testid={MODAL_BACK_BUTTON}
                    className={classNames("modalBack", backButtonClass)}
                    onClick={() => onBack()}
                  >
                    <FontAwesomeIcon icon={faAngleLeft as IconProp} />
                  </button>
                )}
                {hasTitle && (
                  <Dialog.Title
                    data-testid={MODAL_TITLE}
                    className={classNames(
                      "modalTitle",
                      customModalTitleClass,
                      titleClass,
                    )}
                  >
                    {title ?? name}
                  </Dialog.Title>
                )}
                {hasCloseButton && (
                  <Dialog.Close asChild>
                    <button
                      data-testid={MODAL_CLOSE_BUTTON}
                      className={classNames("modalClose", closeButtonClass)}
                    >
                      <ExitIcon />
                    </button>
                  </Dialog.Close>
                )}
              </header>
            </div>
          )}

          <div
            className={classNames(
              "modalContent",
              customModalContentClass,
              contentClass,
            )}
          >
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
